/**
 * Tenant & Organization governance hook (Issue #268).
 *
 * Wraps the typed `@jinzhuan/api-client` GET calls so the `/settings` tenant
 * and organization governance section can render real backend data.
 *
 * Red lines:
 *   - All data comes through `@jinzhuan/api-client` typed client. We never
 *     fake tenant / org rows on the frontend (AI GUIDE §2, §11).
 *   - The section is read-only in the first version; no write hooks are
 *     exposed. New tenant creation, cross-tenant migration, and online
 *     organization editing must arrive via separate Issues.
 *   - Errors are surfaced verbatim from the server — no synthetic success
 *     states, no optimistic local rows.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  createApiClient,
  type GovernanceBoundariesDto,
  type OrganizationUnitDto,
  type TenantOrganizationAuditEventDto,
  type TenantOrganizationCurrentDto,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export interface AsyncState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function pickErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const maybeMessage = (err as { message?: string }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return fallback;
}

export interface TenantOrganizationViewReturn {
  readonly current: AsyncState<TenantOrganizationCurrentDto>;
  readonly units: AsyncState<readonly OrganizationUnitDto[]>;
  readonly governance: AsyncState<GovernanceBoundariesDto>;
  readonly auditEvents: AsyncState<readonly TenantOrganizationAuditEventDto[]>;
  refresh: () => Promise<void>;
}

/**
 * Loads the read-only tenant / organization governance view in parallel:
 *   - GET /api/v1/settings/tenant-organization/current
 *   - GET /api/v1/settings/tenant-organization/units
 *   - GET /api/v1/settings/tenant-organization/governance-boundaries
 *   - GET /api/v1/settings/tenant-organization/audit-events
 *
 * Each slice keeps its own loading/error state so a partial failure does not
 * blank the section.
 */
export function useTenantOrganizationView(): TenantOrganizationViewReturn {
  const [current, setCurrent] = useState<AsyncState<TenantOrganizationCurrentDto>>({
    data: null,
    loading: false,
    error: null,
  });
  const [units, setUnits] = useState<AsyncState<readonly OrganizationUnitDto[]>>({
    data: null,
    loading: false,
    error: null,
  });
  const [governance, setGovernance] = useState<AsyncState<GovernanceBoundariesDto>>({
    data: null,
    loading: false,
    error: null,
  });
  const [auditEvents, setAuditEvents] = useState<
    AsyncState<readonly TenantOrganizationAuditEventDto[]>
  >({
    data: null,
    loading: false,
    error: null,
  });

  const loadCurrent = useCallback(async () => {
    setCurrent((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/settings/tenant-organization/current',
      );
      if (error) {
        setCurrent({
          data: null,
          loading: false,
          error: pickErrorMessage(error, '加载租户与主机构失败'),
        });
        return;
      }
      setCurrent({ data: data ?? null, loading: false, error: null });
    } catch (err) {
      setCurrent({
        data: null,
        loading: false,
        error: pickErrorMessage(err, '加载租户与主机构失败'),
      });
    }
  }, []);

  const loadUnits = useCallback(async () => {
    setUnits((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/settings/tenant-organization/units',
      );
      if (error) {
        setUnits({
          data: null,
          loading: false,
          error: pickErrorMessage(error, '加载组织单元失败'),
        });
        return;
      }
      setUnits({ data: data ?? [], loading: false, error: null });
    } catch (err) {
      setUnits({
        data: null,
        loading: false,
        error: pickErrorMessage(err, '加载组织单元失败'),
      });
    }
  }, []);

  const loadGovernance = useCallback(async () => {
    setGovernance((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/settings/tenant-organization/governance-boundaries',
      );
      if (error) {
        setGovernance({
          data: null,
          loading: false,
          error: pickErrorMessage(error, '加载治理边界失败'),
        });
        return;
      }
      setGovernance({ data: data ?? null, loading: false, error: null });
    } catch (err) {
      setGovernance({
        data: null,
        loading: false,
        error: pickErrorMessage(err, '加载治理边界失败'),
      });
    }
  }, []);

  const loadAuditEvents = useCallback(async () => {
    setAuditEvents((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/settings/tenant-organization/audit-events',
        { params: { query: { limit: 50 } } },
      );
      if (error) {
        setAuditEvents({
          data: null,
          loading: false,
          error: pickErrorMessage(error, '加载初始化审计事件失败'),
        });
        return;
      }
      setAuditEvents({ data: data ?? [], loading: false, error: null });
    } catch (err) {
      setAuditEvents({
        data: null,
        loading: false,
        error: pickErrorMessage(err, '加载初始化审计事件失败'),
      });
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([
      loadCurrent(),
      loadUnits(),
      loadGovernance(),
      loadAuditEvents(),
    ]);
  }, [loadCurrent, loadUnits, loadGovernance, loadAuditEvents]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { current, units, governance, auditEvents, refresh };
}
